import React, { useEffect, useState } from "react";
import {
  Box,
  Button,
  Chip,
  CircularProgress,
  IconButton,
  InputAdornment,
  MenuItem,
  Paper,
  Select,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import CrmClientSelect from "../../../Components/CrmClientSelect";
import AddIcon from "@mui/icons-material/Add";
import CloseIcon from "@mui/icons-material/Close";
import SaveAltIcon from "@mui/icons-material/SaveAlt";
import { FaTrash } from "react-icons/fa";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { DatePicker, LocalizationProvider } from "@mui/x-date-pickers";
import dayjs, { Dayjs } from "dayjs";
import { useNavigate, useParams } from "react-router";
import { useCreateContractMutation, useGetContractsQuery, useGetCustomerQuery } from "../../../Api/attoDeskApi";
import { IContractItem, ICustomer } from "../../../Api/Interface/api.interface";
import { useNotifier } from "../../../Core/Notifier";

const DAY_LABELS = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];

const emptyItem = (): IContractItem => ({
  day_of_week:   null,
  specific_date: null,
  description:   "",
  unit_price:    0,
  buses:         1,
  tax_percent:   13,
});

const ContractCreate: React.FC = () => {
  const { id } = useParams<{ id?: string }>();
  const navigate = useNavigate();
  const { showMessage } = useNotifier();
  const isEdit = Boolean(id);

  // ── API ───────────────────────────────────────────────────────────────────────
  const [createContract, { isLoading: isSaving }] = useCreateContractMutation();
  const { data: customerData } = useGetCustomerQuery({ searchText: "", id: "", quoteWorkOrderCustomerId: "" });
  const { data: existingData, isFetching: isLoadingExisting } = useGetContractsQuery(
    { id: id ?? "" },
    { skip: !isEdit }
  );

  // ── Form state ────────────────────────────────────────────────────────────────
  const [selectedClient, setSelectedClient] = useState<ICustomer | null>(null);
  const [contractName, setContractName]     = useState("");
  const [startDate, setStartDate]           = useState<Dayjs | null>(dayjs());
  const [endDate, setEndDate]               = useState<Dayjs | null>(dayjs().add(1, "year"));
  const [billingType, setBillingType]       = useState<"daily" | "weekly" | "monthly">("daily");
  const [defaultTax, setDefaultTax]         = useState(13);
  const [notes, setNotes]                   = useState("");
  const [items, setItems]                   = useState<IContractItem[]>([emptyItem()]);

  const customers = (customerData?.data as ICustomer[]) ?? [];

  // ── Populate on edit ─────────────────────────────────────────────────────────
  useEffect(() => {
    if (!isEdit || !existingData?.data) return;
    const list = existingData.data as any[];
    if (!list || list.length === 0) return;
    const c = list[0];

    setContractName(c.contract_name ?? "");
    setStartDate(c.start_date ? dayjs(c.start_date) : null);
    setEndDate(c.end_date ? dayjs(c.end_date) : null);
    setBillingType(c.billing_type ?? "daily");
    setDefaultTax(parseFloat(c.default_tax ?? 13));
    setNotes(c.notes ?? "");

    const found = customers.find((x) => String(x.id) === String(c.client_id));
    if (found) setSelectedClient(found);

    if (c.items && c.items.length > 0) {
      setItems(c.items.map((i: any) => ({
        id:            i.id,
        day_of_week:   i.day_of_week !== null && i.day_of_week !== undefined ? Number(i.day_of_week) : null,
        specific_date: i.specific_date ?? null,
        description:   i.description ?? "",
        unit_price:    parseFloat(i.unit_price ?? 0),
        buses:         parseInt(i.buses ?? 1, 10),
        tax_percent:   parseFloat(i.tax_percent ?? 13),
      })));
    }
  }, [isEdit, existingData, customerData]);

  // ── When billing_type changes clear day_of_week for non-weekly ───────────────
  const handleBillingTypeChange = (val: "daily" | "weekly" | "monthly") => {
    setBillingType(val);
    if (val !== "weekly") {
      setItems((prev) => prev.map((i) => ({ ...i, day_of_week: null })));
    }
  };

  // ── Item helpers ─────────────────────────────────────────────────────────────
  const updateItem = (idx: number, field: keyof IContractItem, value: any) => {
    setItems((prev) => {
      const next = [...prev];
      next[idx] = { ...next[idx], [field]: value };
      return next;
    });
  };

  const addItem = () => setItems((prev) => [...prev, { ...emptyItem(), tax_percent: defaultTax }]);
  const removeItem = (idx: number) =>
    setItems((prev) => prev.length === 1 ? prev : prev.filter((_, i) => i !== idx));

  // ── Save ─────────────────────────────────────────────────────────────────────
  const handleSave = async () => {
    if (!selectedClient) { showMessage("Please select a client", "error"); return; }
    if (!contractName.trim()) { showMessage("Please enter a contract name", "error"); return; }
    if (!startDate || !endDate) { showMessage("Please set start and end dates", "error"); return; }
    if (endDate.isBefore(startDate)) { showMessage("End date must be after start date", "error"); return; }

    try {
      const payload = {
        id: isEdit ? id : undefined,
        client_id:    selectedClient.id,
        contract_name: contractName.trim(),
        start_date:   startDate.format("YYYY-MM-DD"),
        end_date:     endDate.format("YYYY-MM-DD"),
        billing_type: billingType,
        default_tax:  defaultTax,
        notes:        notes || null,
        items:        items.map((item, i) => ({ ...item, sort_order: i })),
      };

      const res = await createContract(payload).unwrap();
      if (res.status) {
        showMessage(isEdit ? "Contract updated" : "Contract created", "success");
        navigate("/contract/list");
      } else {
        showMessage(res.message || "Failed to save", "error");
      }
    } catch {
      showMessage("An error occurred while saving", "error");
    }
  };

  if (isEdit && isLoadingExisting) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", mt: 8 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <LocalizationProvider dateAdapter={AdapterDayjs}>
      <Box sx={{ p: { xs: 2, md: 3 } }}>
        {/* ── Top bar ── */}
        <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 3 }}>
          <Typography variant="h5" fontWeight={700} color="#0f172a">
            {isEdit ? "Edit Contract" : "New Recurring Contract"}
          </Typography>
          <Box sx={{ display: "flex", gap: 1.5 }}>
            <Button
              variant="outlined"
              startIcon={<CloseIcon />}
              onClick={() => navigate("/contract/list")}
              sx={{ textTransform: "none", borderRadius: 2 }}
            >
              Cancel
            </Button>
            <Button
              variant="contained"
              startIcon={isSaving ? <CircularProgress size={16} color="inherit" /> : <SaveAltIcon />}
              onClick={handleSave}
              disabled={isSaving}
              sx={{ textTransform: "none", borderRadius: 2, bgcolor: "#2f3891", "&:hover": { bgcolor: "#2f3891" } }}
            >
              {isEdit ? "Update Contract" : "Save Contract"}
            </Button>
          </Box>
        </Box>

        {/* ── Contract details ── */}
        <Paper elevation={0} sx={{ border: "1px solid #e2e8f0", borderRadius: 3, p: 3, mb: 3 }}>
          <Typography fontWeight={600} color="#0f172a" mb={2}>Contract Details</Typography>
          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr", md: "2fr 2fr 1fr 1fr 1fr 1fr" },
              gap: 2,
            }}
          >
            {/* Contract Name */}
            <TextField
              label="Contract Name *"
              size="small"
              value={contractName}
              onChange={(e) => setContractName(e.target.value)}
              placeholder="e.g. ABC School Daily Route"
            />

            {/* Client */}
            <CrmClientSelect
              customers={customers}
              value={selectedClient}
              onChange={setSelectedClient}
              label="Client"
              required
            />

            {/* Start Date */}
            <DatePicker
              label="Start Date *"
              value={startDate}
              onChange={setStartDate}
              slotProps={{ textField: { size: "small" } }}
            />

            {/* End Date */}
            <DatePicker
              label="End Date *"
              value={endDate}
              onChange={setEndDate}
              slotProps={{ textField: { size: "small" } }}
            />

            {/* Billing Type */}
            <TextField
              select
              label="Billing Type"
              size="small"
              value={billingType}
              onChange={(e) => handleBillingTypeChange(e.target.value as any)}
            >
              <MenuItem value="daily">Daily</MenuItem>
              <MenuItem value="weekly">Weekly</MenuItem>
              <MenuItem value="monthly">Monthly</MenuItem>
            </TextField>

            {/* Default Tax */}
            <TextField
              label="Default Tax %"
              size="small"
              type="number"
              inputProps={{ min: 0, step: 0.01 }}
              value={defaultTax}
              onChange={(e) => setDefaultTax(parseFloat(e.target.value) || 0)}
              InputProps={{ endAdornment: <InputAdornment position="end">%</InputAdornment> }}
            />
          </Box>

          {/* Notes */}
          <Box mt={2}>
            <TextField
              label="Notes (optional)"
              size="small"
              fullWidth
              multiline
              rows={2}
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder="Internal notes about this contract…"
            />
          </Box>
        </Paper>

        {/* ── Contract Items ── */}
        <Paper elevation={0} sx={{ border: "1px solid #e2e8f0", borderRadius: 3, overflow: "hidden" }}>
          <Box
            sx={{
              display: "flex", alignItems: "center", justifyContent: "space-between",
              px: 3, py: 2, borderBottom: "1px solid #e2e8f0",
            }}
          >
            <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
              <Typography fontWeight={600} color="#0f172a">
                Billing Items
                <Typography component="span" variant="caption" color="text.secondary" ml={1}>
                  ({items.length} row{items.length !== 1 ? "s" : ""})
                </Typography>
              </Typography>
              {billingType === "weekly" && (
                <Chip
                  label="Set day of week per row"
                  size="small"
                  sx={{ bgcolor: "#ede9fe", color: "#2f3891", fontSize: 11 }}
                />
              )}
            </Box>
            <Button
              size="small"
              startIcon={<AddIcon />}
              onClick={addItem}
              sx={{ textTransform: "none", bgcolor: "#2f3891", color: "#fff", borderRadius: 2, px: 2, "&:hover": { bgcolor: "#2f3891" } }}
            >
              Add Row
            </Button>
          </Box>

          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow sx={{ bgcolor: "#f8fafc" }}>
                  {billingType === "weekly" && (
                    <TableCell sx={{ fontWeight: 600, color: "#64748b", width: 110 }}>Day</TableCell>
                  )}
                  <TableCell sx={{ fontWeight: 600, color: "#64748b" }}>Description</TableCell>
                  <TableCell sx={{ fontWeight: 600, color: "#64748b", width: 80 }} align="right">Buses</TableCell>
                  <TableCell sx={{ fontWeight: 600, color: "#64748b", width: 130 }} align="right">Unit Price</TableCell>
                  <TableCell sx={{ fontWeight: 600, color: "#64748b", width: 100 }} align="right">Tax %</TableCell>
                  <TableCell sx={{ width: 44 }} />
                </TableRow>
              </TableHead>
              <TableBody>
                {items.map((item, idx) => (
                  <TableRow key={idx} hover>
                    {/* Day of week (weekly only) */}
                    {billingType === "weekly" && (
                      <TableCell sx={{ py: 0.5 }}>
                        <TextField
                          select
                          size="small"
                          value={item.day_of_week ?? ""}
                          onChange={(e) => updateItem(idx, "day_of_week", e.target.value === "" ? null : Number(e.target.value))}
                          sx={{ width: 100 }}
                        >
                          <MenuItem value="">Any</MenuItem>
                          {DAY_LABELS.map((d, i) => (
                            <MenuItem key={i} value={i}>{d}</MenuItem>
                          ))}
                        </TextField>
                      </TableCell>
                    )}
                    {/* Description */}
                    <TableCell sx={{ py: 0.5 }}>
                      <TextField
                        size="small"
                        fullWidth
                        placeholder="e.g. Morning Route – Bus A"
                        value={item.description}
                        onChange={(e) => updateItem(idx, "description", e.target.value)}
                        onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); addItem(); } }}
                      />
                    </TableCell>
                    {/* Buses */}
                    <TableCell sx={{ py: 0.5 }} align="right">
                      <TextField
                        size="small"
                        type="number"
                        inputProps={{ min: 1, step: 1, style: { textAlign: "right" } }}
                        sx={{ width: 72 }}
                        value={item.buses}
                        onChange={(e) => updateItem(idx, "buses", parseInt(e.target.value) || 1)}
                      />
                    </TableCell>
                    {/* Unit Price */}
                    <TableCell sx={{ py: 0.5 }} align="right">
                      <TextField
                        size="small"
                        type="number"
                        inputProps={{ min: 0, step: 0.01, style: { textAlign: "right" } }}
                        sx={{ width: 120 }}
                        value={item.unit_price}
                        InputProps={{ startAdornment: <InputAdornment position="start">$</InputAdornment> }}
                        onChange={(e) => updateItem(idx, "unit_price", parseFloat(e.target.value) || 0)}
                      />
                    </TableCell>
                    {/* Tax % */}
                    <TableCell sx={{ py: 0.5 }} align="right">
                      <TextField
                        size="small"
                        type="number"
                        inputProps={{ min: 0, step: 0.01, style: { textAlign: "right" } }}
                        sx={{ width: 88 }}
                        value={item.tax_percent}
                        InputProps={{ endAdornment: <InputAdornment position="end">%</InputAdornment> }}
                        onChange={(e) => updateItem(idx, "tax_percent", parseFloat(e.target.value) || 0)}
                      />
                    </TableCell>
                    {/* Delete */}
                    <TableCell sx={{ py: 0.5 }} align="center">
                      <Tooltip title="Remove row">
                        <IconButton
                          size="small"
                          onClick={() => removeItem(idx)}
                          sx={{ color: "#ef4444" }}
                          disabled={items.length === 1}
                        >
                          <FaTrash size={13} />
                        </IconButton>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </Paper>
      </Box>
    </LocalizationProvider>
  );
};

export default ContractCreate;
